import { useParams, Link, Navigate } from "react-router-dom";
import { Leaf, ShieldCheck, Droplets, Recycle, CheckCircle2 } from "lucide-react";
import { getCollateral } from "../../lib/collateral";
import { COMPANY, IMAGES } from "../../lib/data";
import { B, CollateralBar } from "../../components/site/CollateralKit";

const P_ICONS = [ShieldCheck, Droplets, Recycle];

export default function ProductComplianceBrief() {
  const { product } = useParams();
  const c = getCollateral(product);
  if (!c) return <Navigate to="/solutions" replace />;
  const cp = c.compliance;
  const pdf = `/${c.slug}-compliance-brief.pdf`;

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <CollateralBar product={c.slug} label="Environmental & Compliance · One-Page" pdf={pdf} testid="compliance" />
      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="collateral-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">{c.name} · Environmental & Compliance</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[31px]">{cp.headline}</h1>
              <p className="mt-2 text-charcoal text-[11px] leading-snug">{cp.sub}</p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3 grid grid-cols-3 gap-2 text-center">
              {cp.chips.map(([v, l]) => (<div key={l}><Leaf className="h-4 w-4 text-flame mx-auto" /><div className="font-display font-extrabold text-flame text-[14px] leading-none mt-1">{v}</div><div className="text-[7px] uppercase tracking-wider text-white/50 mt-0.5">{l}</div></div>))}
            </div>
          </header>

          <div className="flex-1 grid grid-cols-3 gap-4 pt-4 min-h-0">
            {cp.pillars.map((p, i) => {
              const Ic = P_ICONS[i % P_ICONS.length];
              return (
                <div key={p.title} className="border-2 border-flame flex flex-col">
                  <div className="bg-flame text-white px-4 py-2 flex items-center gap-2"><Ic className="h-4 w-4" /><span className="font-display font-bold text-[12px] uppercase tracking-wider">{p.title}</span></div>
                  <ul className="p-4 space-y-2.5 flex-1 flex flex-col justify-center">
                    {p.rows.map((x) => (<li key={x} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" /><span><B t={x} /></span></li>))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[13px] uppercase tracking-wider">Why It Matters to Your Program</span></div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-white/70 text-[10px]">
              {cp.why.map(([b, t]) => (<div key={b} className="bg-white/5 border border-white/10 px-4 py-2.5"><span className="text-flame font-semibold">{b}</span> {t}</div>))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div><div className="font-display font-extrabold text-ink text-[15px]">Need documentation for your submittal or permit?</div><div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div></div>
            <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Request Compliance Docs</Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Environmental &amp; compliance statements are representative; confirm certified documentation &amp; local permit requirements. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
